import './styles/graphic.scss';

import { select } from 'd3-selection';
import { scaleLinear, scaleBand } from 'd3-scale';
import { axisLeft, axisBottom } from 'd3-axis';
import { max } from 'd3-array';
import { color } from 'd3-color';
// Side-effect import: patches selection.prototype.transition. Without this,
// any `.transition()` call on a d3-selection throws "is not a function".
import 'd3-transition';

import { aggregateByMonth, aggregateByWeekday } from './utils/data';
import { CATEGORIES, STACK_ORDER, colorFor } from './utils/categories';

const FONT = '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';
const GRAY_TEXT = '#262626';
const GRAY_AXIS = '#A6A6A6';
const GRAY_GRID = '#ececec';
const GRAY_BASELINE = '#bcbcbc';
const TRANSITION_MS = 450;

// Kept at module scope (not inside the exported function) so toggle state
// survives a pym-triggered resize, which re-invokes the default export.
const state = {
  showOrange: false,
  showBrown: false,
  mode: 'count', // 'count' | 'percent'
};

function isVisible(key) {
  if (key === 'orange') return state.showOrange;
  if (key === 'brown') return state.showBrown;
  return true; // gray is always shown, it's the baseline
}

// Turns one bucket ({ gray, orange, brown, total }) into stacked segments
// for whichever categories are currently toggled on. Percent mode divides
// by the bucket's original (full) total, not the visible subset, so hiding
// a category visibly shrinks the bar below 100% by exactly its share.
// `raw`/`total` ride along on every segment (regardless of display mode)
// so hover annotations can always report the category's true percentage
// share of the bucket, not just whatever `value` happens to represent.
function computeSegments(bucket) {
  const visibleKeys = STACK_ORDER.filter(isVisible);
  let cumulative = 0;
  return visibleKeys.map((key) => {
    const raw = bucket[key];
    const value = state.mode === 'percent' ? (raw / bucket.total) * 100 : raw;
    const y0 = cumulative;
    const y1 = cumulative + value;
    cumulative = y1;
    return {
      key, value, y0, y1, raw, total: bucket.total,
    };
  });
}

// Percentage share a single segment represents of its bucket's total,
// independent of count/percent display mode.
function percentShare(seg) {
  if (!seg.total) return 0;
  return Math.round((seg.raw / seg.total) * 100);
}

// Text shown in a hover annotation pill: the segment's raw count in count
// mode, its percentage share in percent mode. Mirrors `formatValue`, but
// reads a segment's `raw`/`total` directly so it stays correct even though
// hover pills for every same-category row are shown at once.
function hoverLabel(seg) {
  return state.mode === 'percent' ? `${percentShare(seg)}%` : `${seg.raw}`;
}

function formatValue(value) {
  const rounded = Math.round(value);
  return state.mode === 'percent' ? `${rounded}%` : `${rounded}`;
}

// Builds one stacked-bar panel (month or weekday) and returns a `render()`
// function the caller re-invokes whenever the toggle/mode state changes.
// Rows are the categories (months or weekdays); bars run left-to-right.
function drawPanel(container, {
  buckets, maxTotal, width, isMobile, title,
}) {
  const rowHeight = isMobile ? 24 : 28;
  const margin = {
    top: 6, right: 36, bottom: 26, left: isMobile ? 64 : 78,
  };
  const innerWidth = Math.max(10, width - margin.left - margin.right);
  const innerHeight = buckets.length * rowHeight;
  const chartHeight = innerHeight + margin.top + margin.bottom;

  const panel = container.append('div').attr('class', 'shs-panel');
  panel.append('p').attr('class', 'shs-panel-title').text(title);

  const svg = panel.append('svg')
    .attr('class', 'shs-panel-svg')
    .attr('width', width)
    .attr('height', chartHeight);

  const g = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  const yScale = scaleBand()
    .domain(buckets.map(b => b.label))
    .range([0, innerHeight])
    .padding(0.28);

  const axisLabelByKey = new Map(
    buckets.map(b => [b.label, isMobile ? b.axisLabelMobile : b.axisLabel]),
  );

  g.append('line')
    .attr('class', 'shs-baseline')
    .attr('x1', 0)
    .attr('x2', 0)
    .attr('y1', 0)
    .attr('y2', innerHeight)
    .attr('stroke', GRAY_BASELINE)
    .attr('stroke-width', 1)
    .attr('shape-rendering', 'crispEdges');
  // Appended before the bars/labels groups so its gridlines paint
  // underneath them instead of over top.
  const xAxisGroup = g.append('g')
    .attr('class', 'shs-x-axis')
    .attr('transform', `translate(0,${innerHeight})`);
  const barsGroup = g.append('g').attr('class', 'shs-bars');
  const labelsGroup = g.append('g').attr('class', 'shs-bar-labels');
  const yAxisGroup = g.append('g').attr('class', 'shs-y-axis');

  // Hover annotations: one pill (background rect + text) per row that
  // shares the hovered category, so hovering one brown/gray segment can
  // light up every brown/gray row at once. Data-joined against whichever
  // segments are currently "active" rather than a single fixed element,
  // since the count of simultaneously-visible pills varies. Lives in its
  // own group so `.raise()` can put every pill above all bars.
  const hoverAnnotationsGroup = g.append('g')
    .attr('class', 'shs-hover-annotations')
    .style('pointer-events', 'none');

  function updateHoverAnnotations(segments, xScale) {
    const paddingX = 6;
    const paddingY = 3;

    const pills = hoverAnnotationsGroup.selectAll('g.shs-hover-annotation')
      .data(segments, seg => seg.label);

    pills.exit().remove();

    const entered = pills.enter().append('g').attr('class', 'shs-hover-annotation');
    entered.append('rect')
      .attr('class', 'shs-hover-annotation-bg')
      .attr('rx', 3)
      .attr('ry', 3);
    entered.append('text')
      .attr('class', 'shs-hover-annotation-text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.32em')
      .style('font-family', FONT)
      .style('font-size', '11px')
      .style('font-weight', 700);

    entered.merge(pills).each(function positionPill(seg) {
      const pill = select(this);
      const text = pill.select('text').text(hoverLabel(seg));
      const bbox = text.node().getBBox();
      const boxWidth = bbox.width + paddingX * 2;
      const boxHeight = bbox.height + paddingY * 2;

      const rawMidX = (xScale(seg.y0) + xScale(seg.y1)) / 2;
      const midX = Math.min(
        Math.max(rawMidX, boxWidth / 2),
        innerWidth - boxWidth / 2,
      );
      // Centered on the bar itself (not floating above it) so it's
      // unambiguous which row's segment the pill belongs to.
      const rowCenter = yScale(seg.label) + (yScale.bandwidth() / 2);
      const boxY = rowCenter - (boxHeight / 2);

      pill.select('rect')
        .attr('x', midX - boxWidth / 2)
        .attr('y', boxY)
        .attr('width', boxWidth)
        .attr('height', boxHeight);
      text
        .attr('x', midX)
        .attr('y', boxY + boxHeight / 2);
    });

    hoverAnnotationsGroup.raise();
  }

  function clearHoverAnnotations() {
    hoverAnnotationsGroup.selectAll('g.shs-hover-annotation').remove();
  }

  // Row (category) labels never change with toggle/mode state, so draw
  // them once rather than inside render().
  const yAxis = axisLeft(yScale).tickFormat(d => axisLabelByKey.get(d) || d);
  yAxisGroup.call(yAxis);
  yAxisGroup.select('.domain').remove();
  yAxisGroup.selectAll('.tick line').remove();
  yAxisGroup.selectAll('.tick text')
    .attr('fill', GRAY_TEXT)
    .style('font-size', '12px')
    .style('font-weight', 400)
    .style('font-family', FONT);

  function render() {
    // Toggle/mode changes rebuild segments and can remove whatever segment(s)
    // were hovered, so clear any lingering annotations before re-rendering.
    clearHoverAnnotations();

    const isPercent = state.mode === 'percent';
    const xDomainMax = isPercent ? 100 : maxTotal;
    const xScale = scaleLinear().domain([0, xDomainMax]).nice().range([0, innerWidth]);

    const tickCount = isMobile ? 3 : 4;
    const tickValues = xScale.ticks(tickCount);

    const xAxis = axisBottom(xScale)
      .tickValues(tickValues)
      .tickSize(-innerHeight)
      .tickFormat((d, i) => {
        const isTopTick = i === tickValues.length - 1;
        if (isPercent) return isTopTick ? `${d}%` : `${d}`;
        return `${d}`;
      });

    xAxisGroup.call(xAxis);
    xAxisGroup.select('.domain').remove();
    xAxisGroup.selectAll('.tick line')
      .attr('stroke', GRAY_GRID)
      .attr('stroke-width', 0.75)
      .attr('shape-rendering', 'crispEdges');
    xAxisGroup.selectAll('.tick text')
      .attr('fill', GRAY_AXIS)
      .style('font-size', '12px')
      .style('font-weight', 300)
      .style('font-family', FONT);

    // Segment rects, keyed by bucket + category so hidden categories exit
    // (shrink to nothing) and re-entering ones grow back in.
    const segData = [];
    buckets.forEach((bucket) => {
      computeSegments(bucket).forEach((seg) => {
        segData.push({ ...seg, label: bucket.label });
      });
    });

    const rects = barsGroup.selectAll('rect')
      .data(segData, d => `${d.label}-${d.key}`)
      .join(
        enter => enter.append('rect')
          .attr('x', 0)
          .attr('width', 0)
          .attr('y', d => yScale(d.label))
          .attr('height', yScale.bandwidth())
          .attr('fill', d => colorFor(d.key)),
        update => update,
        exit => exit.transition().duration(TRANSITION_MS)
          .attr('x', 0)
          .attr('width', 0)
          .remove(),
      );

    // Hover highlight + annotation, scoped to every segment sharing the
    // hovered category (not just the one rect under the cursor): the
    // hovered category darkens and stays fully opaque, every other
    // category fades out, and pills appear on each of the hovered
    // category's segments. Active in both count and percent modes; only
    // the pill's text (via `hoverLabel`) changes between them. Re-attached
    // on every render (toggle/mode changes rebuild `segData`, so handlers
    // must rebind to pick up each segment's current y0/y1/raw/total).
    rects
      .on('mouseenter', (event, d) => {
        barsGroup.selectAll('rect')
          .attr('fill', dd => (dd.key === d.key ? color(colorFor(dd.key)).darker(0.6) : colorFor(dd.key)))
          .style('opacity', dd => (dd.key === d.key ? 1 : 0.25));
        updateHoverAnnotations(segData.filter(seg => seg.key === d.key), xScale);
      })
      .on('mouseleave', () => {
        barsGroup.selectAll('rect')
          .attr('fill', dd => colorFor(dd.key))
          .style('opacity', 1);
        clearHoverAnnotations();
      });

    rects
      .transition()
      .duration(TRANSITION_MS)
      .attr('x', d => xScale(d.y0))
      .attr('width', d => Math.max(0, xScale(d.y1) - xScale(d.y0)))
      .attr('y', d => yScale(d.label))
      .attr('height', yScale.bandwidth())
      .attr('fill', d => colorFor(d.key));

    // Bold total-value label just past the end of each bar. Skipped in
    // percent mode, since the visible share always ends at (or, once a
    // category is hidden, somewhere unlabeled-but-obvious below) 100.
    const totals = isPercent ? [] : buckets.map((bucket) => {
      const segments = computeSegments(bucket);
      const topValue = segments.length ? segments[segments.length - 1].y1 : 0;
      return { label: bucket.label, value: topValue };
    });

    labelsGroup.selectAll('text')
      .data(totals, d => d.label)
      .join(
        enter => enter.append('text')
          .attr('text-anchor', 'start')
          .attr('fill', GRAY_TEXT)
          .style('font-size', '12px')
          .style('font-weight', 700)
          .style('font-family', FONT)
          .attr('x', 0)
          .attr('y', d => yScale(d.label) + (yScale.bandwidth() / 2) + 4),
        update => update,
        exit => exit.remove(),
      )
      .attr('y', d => yScale(d.label) + (yScale.bandwidth() / 2) + 4)
      .transition()
      .duration(TRANSITION_MS)
      .attr('x', d => xScale(d.value) + 6)
      .text(d => formatValue(d.value));
  }

  return render;
}

export default function (props, container, isMobile) {
  const { initialWidth } = props;
  const width = Math.min(initialWidth, 780);

  container.html('');
  const root = container.append('div').attr('class', 'shs');

  // ---- controls: legend pills double as toggles, plus count/% switch ----
  const controls = root.append('div').attr('class', 'shs-controls');
  const legend = controls.append('div').attr('class', 'shs-legend');

  const legendItems = {};
  CATEGORIES.forEach((cat) => {
    const item = legend.append(cat.toggleable ? 'button' : 'div')
      .attr('class', `shs-legend-item shs-legend-item--${cat.key}`);
    if (cat.toggleable) item.attr('type', 'button');
    item.append('span').attr('class', 'shs-swatch').style('background-color', cat.color);
    item.append('span').attr('class', 'shs-legend-label').text(cat.label);
    legendItems[cat.key] = item;
  });

  const modeSwitch = controls.append('div').attr('class', 'shs-mode-switch');
  const countBtn = modeSwitch.append('button')
    .attr('type', 'button').attr('class', 'shs-mode-btn').text('Count');
  const pctBtn = modeSwitch.append('button')
    .attr('type', 'button').attr('class', 'shs-mode-btn').text('%');

  controls.append('p').attr('class', 'shs-hint')
    .text('Click "Football and homecoming" or "Basketball" to see how these incidents contribute to the trend');

  // ---- panels ----
  const monthBuckets = aggregateByMonth();
  const weekdayBuckets = aggregateByWeekday();
  // Both panels share one x-axis scale (based on the larger of the two
  // maxes) so their bars/gridlines are visually comparable to each other.
  const sharedMax = Math.max(
    max(monthBuckets, b => b.total),
    max(weekdayBuckets, b => b.total),
  );

  const monthPanelContainer = root.append('div').attr('class', 'shs-panel-wrap');
  const weekdayPanelContainer = root.append('div').attr('class', 'shs-panel-wrap');

  const renderMonth = drawPanel(monthPanelContainer, {
    buckets: monthBuckets, maxTotal: sharedMax, width, isMobile, title: 'By month',
  });
  const renderWeekday = drawPanel(weekdayPanelContainer, {
    buckets: weekdayBuckets, maxTotal: sharedMax, width, isMobile, title: 'By day of week',
  });

  function sendHeight() {
    if (window.pymChild) window.pymChild.sendHeight();
  }

  function updateControls() {
    CATEGORIES.forEach((cat) => {
      if (!cat.toggleable) return;
      legendItems[cat.key].classed('is-off', !isVisible(cat.key));
    });
    countBtn.classed('is-active', state.mode === 'count');
    pctBtn.classed('is-active', state.mode === 'percent');
  }

  function update() {
    updateControls();
    renderMonth();
    renderWeekday();
    sendHeight();
    setTimeout(sendHeight, TRANSITION_MS + 50);
  }

  legendItems.orange.on('click', () => {
    state.showOrange = !state.showOrange;
    update();
  });
  legendItems.brown.on('click', () => {
    state.showBrown = !state.showBrown;
    update();
  });
  countBtn.on('click', () => {
    if (state.mode === 'count') return;
    state.mode = 'count';
    update();
  });
  pctBtn.on('click', () => {
    if (state.mode === 'percent') return;
    state.mode = 'percent';
    update();
  });

  update();
}
